/* eslint-disable no-unused-vars */
/* eslint-disable react/jsx-curly-newline */
/* eslint-disable import/order */
/* eslint-disable max-len */
import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useDispatch, useSelector } from "react-redux";

import {
  selectAxisOptions,
  selectAxisSelection,
  selectCartesianConfiguration,
  selectCartesianCurrentStep,
  selectCartesianReadOnlyMode,
  selectCartesianSteps,
  selectDynamics,
  selectKinematics,
  selectMechanics,
  setCartesianCadenasProductCode,
  setCartesianCurrentStep,
  setCartesianProductCode
} from "../../../store/slices/cartesian";
import { selectUpdateConfiguration, setUpdatingConfiguration, updateConfiguration } from "../../../store/slices/common";
import { updateConfigurationApi } from "../../../utils/common/api/api";
import t from "../../../utils/common/translation/translator";
import useCheckMobileScreen from "../../../utils/common/useCheckMobileScreen";

import Stepper from "shared-components/lib/iac-stepper";
import {
  buildVirtualPageViewDataLayerEvent,
  logDataLayerEvent
} from "../../../utils/common/dataLayerAnalytics/dataLayerAnalytics";

/**
 * Sub header of the app.
 */
const CartesianStepper = () => {
  const steps = useSelector(selectCartesianSteps);
  const currentStep = useSelector(selectCartesianCurrentStep);
  const configuration = useSelector(selectCartesianConfiguration);
  const dispatch = useDispatch();
  const isMobile = useCheckMobileScreen();

  useEffect(() => {
    logDataLayerEvent(buildVirtualPageViewDataLayerEvent(configuration?.configId, currentStep?.label));
  }, [currentStep]);

  const runUpdateConfiguration = useSelector(selectUpdateConfiguration);

  const modeReadOnly = useSelector(selectCartesianReadOnlyMode);
  const queryClient = useQueryClient();

  const [savedConfigurationPayload, setSavedConfigurationPayload] = useState(null);

  const {
    refetch: execUpdateConfiguration,
    isLoading,
    isFetching
  } = useQuery(
    "UPDATE_CARTESIAN_CONFIGURATION",
    async () => {
      try {
        if (JSON.stringify(savedConfigurationPayload) !== JSON.stringify(configuration.configData)) {
          setSavedConfigurationPayload(configuration.configData);
          dispatch(setCartesianProductCode(""));
          dispatch(setCartesianCadenasProductCode(""));
          const updatedConfig = (await updateConfigurationApi(configuration.configId, configuration.configData)) || {};
          if (updatedConfig) {
            const updatedProductCode = updatedConfig.updatedConfigurationData.configData.productCode;
            const updatedCadenasProductCode = updatedConfig.updatedConfigurationData.configData.cadenasProductCode;
            dispatch(setCartesianProductCode(updatedProductCode));
            dispatch(setCartesianCadenasProductCode(updatedCadenasProductCode));
          }
          return updatedConfig;
        }

        return null;
      } catch (error) {
        console.error("ERR", error);
      }
      return undefined;
    },
    {
      enabled: false
    }
  );

  useEffect(() => {
    dispatch(setUpdatingConfiguration(isLoading || isFetching));
  }, [isLoading, isFetching]);

  useEffect(() => {
    if (configuration?.configId && currentStep && !modeReadOnly) {
      // console.log("Configuration changed => update CONF");
      queryClient.cancelQueries("UPDATE_CONFIGURATION");
      execUpdateConfiguration();
    }
  }, [runUpdateConfiguration, configuration]);

  const handleSelectionSettings = (step) => {
    dispatch(setCartesianCurrentStep(step));
    dispatch(updateConfiguration());
  };

  const kinematics = useSelector(selectKinematics);
  const mechanics = useSelector(selectMechanics);
  const dynamics = useSelector(selectDynamics);
  const axisSelection = useSelector(selectAxisSelection);
  const axisOptions = useSelector(selectAxisOptions);

  const isKinematicsValid = () => kinematics.isValid;
  const isMechanicsValid = () => mechanics.isValid;
  const isDynamicsValid = () => dynamics.isValid;
  const isAxisSelectionValid = () => axisSelection.isValid;
  const isAxisOptionsValid = () => axisOptions.isValid;

  const handleGoToMechanics = () => {
    if (isKinematicsValid()) {
      dispatch(setCartesianCurrentStep(steps.MECHANICS));
      dispatch(updateConfiguration());
    }
  };

  const handleGoToDynamics = () => {
    if (isKinematicsValid() && isMechanicsValid()) {
      dispatch(setCartesianCurrentStep(steps.DYNAMICS));
      dispatch(updateConfiguration());
    }
  };

  const handleGoToAxisSelection = () => {
    if (isKinematicsValid() && isMechanicsValid() && isDynamicsValid()) {
      dispatch(setCartesianCurrentStep(steps.AXIS_SELECTION));
      dispatch(updateConfiguration());
    }
  };

  const handleGoToAxisOption = () => {
    if (isKinematicsValid() && isMechanicsValid() && isDynamicsValid() && isAxisSelectionValid()) {
      dispatch(setCartesianCurrentStep(steps.AXIS_OPTIONS));
      dispatch(updateConfiguration());
    }
  };

  const cartesianStepper = currentStep !== steps.START && (
    <>
      <Stepper
        steps={[
          {
            action: () => handleSelectionSettings(steps.KINEMATICS),
            active: true,
            done: isKinematicsValid(),
            hidden: false,
            id: "KINEMATICS",
            isActiveStep: currentStep === steps.KINEMATICS,
            label: t(steps.KINEMATICS.label),
            name: "KINEMATICS",
            order: 1,
            skipped: false,
            subLabel:
              currentStep === steps.KINEMATICS
                ? t("msizer.ui.stepper.in_progress")
                : configuration?.configData?.kinematics?.type
          },
          {
            action: () => handleGoToMechanics(),
            active: isKinematicsValid(),
            done: isMechanicsValid(),
            hidden: false,
            id: "MECHANICS",
            isActiveStep: currentStep === steps.MECHANICS,
            label: t(steps.MECHANICS.label),
            name: "MECHANICS",
            order: 2,
            skipped: false,
            subLabel:
              currentStep === steps.MECHANICS
                ? t("msizer.ui.stepper.in_progress")
                : isMechanicsValid()
                ? t("msizer.ui.stepper.done")
                : t("msizer.ui.stepper.not_done")
          },
          {
            action: () => handleGoToDynamics(),
            active: isKinematicsValid() && isMechanicsValid(),
            done: isDynamicsValid(),
            hidden: false,
            id: "DYNAMICS",
            isActiveStep: currentStep === steps.DYNAMICS,
            label: t(steps.DYNAMICS.label),
            name: "DYNAMICS",
            order: 3,
            skipped: false,
            subLabel:
              currentStep === steps.DYNAMICS
                ? t("msizer.ui.stepper.in_progress")
                : isDynamicsValid()
                ? t("msizer.ui.stepper.done")
                : t("msizer.ui.stepper.not_done")
          },
          {
            action: () => handleGoToAxisSelection(),
            active: isKinematicsValid() && isMechanicsValid() && isDynamicsValid(),
            done: isAxisSelectionValid(),
            hidden: false,
            id: "AXIS_SELECTION",
            isActiveStep: currentStep === steps.AXIS_SELECTION,
            label: t(steps.AXIS_SELECTION.label),
            name: "AXIS_SELECTION",
            order: 4,
            skipped: false,
            subLabel:
              currentStep === steps.AXIS_SELECTION
                ? t("msizer.ui.stepper.in_progress")
                : isAxisSelectionValid()
                ? t("msizer.ui.stepper.done")
                : t("msizer.ui.stepper.not_done")
          },
          {
            action: () => handleGoToAxisOption(),
            active: isKinematicsValid() && isMechanicsValid() && isDynamicsValid() && isAxisSelectionValid(),
            done: isAxisOptionsValid(),
            hidden: false,
            id: "AXIS_OPTIONS",
            isActiveStep: currentStep === steps.AXIS_OPTIONS,
            label: t(steps.AXIS_OPTIONS.label),
            name: "AXIS_OPTIONS",
            order: 5,
            skipped: false,
            subLabel:
              currentStep === steps.AXIS_OPTIONS
                ? t("msizer.ui.stepper.in_progress")
                : isAxisOptionsValid()
                ? t("msizer.ui.stepper.done")
                : t("msizer.ui.stepper.not_done")
          },
          {
            action: () => handleSelectionSettings(steps.FINALIZE_CARTESIAN),
            active:
              isKinematicsValid() &&
              isMechanicsValid() &&
              isDynamicsValid() &&
              isAxisSelectionValid() &&
              isAxisOptionsValid(),
            done: currentStep === steps.FINALIZE_CARTESIAN,
            hidden: false,
            id: "FINALIZE_CARTESIAN",
            isActiveStep: currentStep === steps.FINALIZE_CARTESIAN,
            label: t(steps.FINALIZE_CARTESIAN.label),
            name: "FINALIZE_CARTESIAN",
            order: 6,
            skipped: false,
            subLabel:
              currentStep === steps.FINALIZE_CARTESIAN ? t("msizer.ui.stepper.done") : t("msizer.ui.stepper.not_done")
          }
        ]}
        mobileBreakpoint={800}
        disabled={false}
      ></Stepper>
    </>
  );

  return cartesianStepper;
};

CartesianStepper.propTypes = {};

CartesianStepper.defaultProps = {};

export default CartesianStepper;
